import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query as fsQuery,
  where,
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { useFirebase } from "@/context/firebase-context";
import { useLibraryReport } from "@/hooks/use-library-report";
import { recordRecentSearch } from "@/hooks/use-recent-searches";
import { db } from "@/integrations/firebase/firestore";
import {
  trackSearchCompleted,
  trackSearchFailed,
  trackSearchSubmitted,
} from "@/lib/analytics";
import { normalizeQueryKey } from "@/routes/library-alias";
import {
  canReuseCompletedSearch,
  pickReusableSearch,
  resolveReportState,
  type ReusableSearchRow,
} from "@/routes/report-source";
import type { DrugSearch } from "@/types/report";

// Search orchestration for the home/search page, extracted so the route
// component stays a thin view switcher. The URL (decodedQuery) is the source
// of truth: navigating to /<query> starts a search, navigating back to "/"
// clears everything. Two serve paths:
//  - library fast path: the (client-readable) alias map resolves the query and
//    we stream the report straight from drugs/{id} — no drug_searches doc, no
//    function invocation, no cold start;
//  - backend flow: create (or reattach to) a drug_searches doc and stream its
//    status/progress/results.
interface UseDrugSearchArgs {
  user: User | null;
  // The decoded /:query URL segment; "" on the bare homepage.
  decodedQuery: string;
  // The human label currently committed in the search box — used only to label
  // the recent-searches history entry.
  committedLabel: string | null;
}

export function useDrugSearch({
  user,
  decodedQuery,
  committedLabel,
}: UseDrugSearchArgs) {
  const [drugSearch, setDrugSearch] = useState<DrugSearch | null>(null);
  // Null = fast path inactive (the backend flow owns the screen).
  const [fastPathDrugId, setFastPathDrugId] = useState<string | null>(null);
  const [searchUiError, setSearchUiError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const { addDocument, subscribeToDocument } = useFirebase();

  // Track the query we last kicked off a backend search for, so re-renders /
  // input edits don't re-submit the same URL-driven search.
  const lastSearchedRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  // Alias id whose dangling-alias fallback already ran (see the effect below).
  const fastPathFallbackRef = useRef<string | null>(null);
  // "docId:status" of the search outcome already reported to analytics, so a
  // completed/failed snapshot stream fires the event exactly once per doc.
  const trackedOutcomeRef = useRef<string | null>(null);

  // The LIBRARY (drugs/{id}/reports/v{n}) is the only place a report body
  // lives — a drug_searches doc carries just a pointer (drug_id +
  // report_version), so there is nothing else to render from. The streamed drug
  // is the fast path's alias hit when active, else the drug the backend served.
  const completedStatus = drugSearch?.status;
  const servedDrugId = drugSearch?.results?.drug_id;
  const activeLibraryDrugId =
    fastPathDrugId ?? (completedStatus === "completed" ? servedDrugId : null);
  const {
    libraryReport,
    loading: libraryLoading,
    found: libraryFound,
  } = useLibraryReport(activeLibraryDrugId);
  // "loading" until the library listener settles, so a completed search never
  // flashes an empty panel while its report streams in; "missing" only once we
  // KNOW the library holds nothing, which the fallback effects below turn into a
  // regeneration.
  const reportState = resolveReportState({
    drugId: activeLibraryDrugId,
    libraryReport,
    loading: libraryLoading,
    found: libraryFound,
  });
  const displayReport = libraryReport?.report;
  const displayReportVersion = libraryReport?.version;
  const displayGeneratedAt = libraryReport?.generatedAt;

  // A completed search whose pointer resolves to nothing in the library: the
  // drug was purged / never re-seeded, or the doc predates the pointer contract
  // and carries no drug_id at all. Either way it is a cache miss, not something
  // to paint.
  const completedWithoutLibraryReport =
    completedStatus === "completed" &&
    fastPathDrugId === null &&
    (!servedDrugId || reportState === "missing");

  // The fast path has no drug_searches doc, so these stand in for the backend
  // flow's pending/completed statuses while the library stream settles.
  const fastPathLoading = fastPathDrugId !== null && reportState === "loading";
  const fastPathReady = fastPathDrugId !== null && reportState === "ready";
  const isLoading =
    drugSearch?.status === "pending" ||
    drugSearch?.status === "processing" ||
    fastPathLoading ||
    // Completed, but the report body is still streaming — or a regeneration is
    // about to be scheduled for an orphaned pointer. Keeping the loader up over
    // both avoids a blank frame between the status flip and the report landing.
    (completedStatus === "completed" && reportState === "loading") ||
    completedWithoutLibraryReport;

  // Record history when a search completes (a failed resolution never becomes
  // a chip) — for the backend flow on status "completed", for the fast path
  // when the library report lands. Keyed by query, so a repeat search just
  // bumps its recency; the label prefers what the box shows (the picked /
  // reconstructed Latin label), falling back to the library report's drug name.
  const libraryDrugName = libraryReport?.report.drug_name;
  useEffect(() => {
    if (!user || !decodedQuery) return;
    if (completedStatus !== "completed" && !fastPathReady) return;
    const label = committedLabel?.trim() || libraryDrugName || decodedQuery;
    void recordRecentSearch(user.uid, decodedQuery, label);
  }, [
    user,
    decodedQuery,
    completedStatus,
    committedLabel,
    fastPathReady,
    libraryDrugName,
  ]);

  // Does the library actually hold a report for this drug id? A completed search
  // is only worth reattaching to if it does — its results are a pointer, so a
  // drug that is no longer in `drugs` makes the whole doc unrenderable.
  // An unreadable read counts as "no": a needless regeneration is cheap, whereas
  // reattaching to a dead pointer strands the doctor.
  const hasLibraryReport = async (drugId?: string): Promise<boolean> => {
    if (!drugId) return false;
    try {
      const drugSnap = await getDoc(doc(db, "drugs", drugId));
      const latest = drugSnap.data()?.latest_version as number | undefined;
      return canReuseCompletedSearch(latest);
    } catch (error) {
      console.error("Library presence check failed:", error);
      return false;
    }
  };

  // Reattach to an existing search for this exact query (same user) instead of
  // always creating a new doc. Prefer fresh in-flight work (pickReusableSearch
  // drops a doc the backend can no longer be running); otherwise reuse the
  // newest completed report so a refresh of /:drug paints immediately instead
  // of re-running the whole serve path. Retries pass forceNew to start clean.
  const findReusableSearch = async (query: string): Promise<string | null> => {
    try {
      const snap = await getDocs(
        fsQuery(
          collection(db, "drug_searches"),
          where("user_id", "==", user!.uid),
          where("query", "==", query),
          where("status", "in", ["pending", "processing", "completed"]),
        ),
      );
      if (snap.empty) return null;

      const rows: ReusableSearchRow[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<ReusableSearchRow, "id">),
      }));
      const pick = pickReusableSearch(rows, Date.now());
      if (!pick) return null;
      if (pick.status !== "completed") return pick.id;

      // The library is the source of truth: reattach only if it still holds a
      // report for the drug this search served. Returning null forces a fresh
      // drug_searches doc, whose server run regenerates and repopulates the
      // library — which is exactly what should happen for a drug that was purged
      // or never re-seeded. (Before this check, such a search reattached and
      // painted its embedded snapshot forever, so a doctor could open a report
      // the backoffice library no longer listed.) A newer library version needs
      // no special case any more: the display path always streams the latest.
      if (!(await hasLibraryReport(pick.results?.drug_id))) return null;
      return pick.id;
    } catch (error) {
      console.error("Reusable-search lookup failed:", error);
      return null;
    }
  };

  // Client-side cache-hit fast path: resolve the query via the read-only alias
  // map (query_key -> drug_id) and stream the library report directly — no
  // drug_searches doc, no function invocation, no cold start. Any miss or
  // failure (offline, rules not yet deployed) returns null and the backend
  // path — which also handles synonym/brand identity resolution — takes over.
  const lookupLibraryDrugId = async (query: string): Promise<string | null> => {
    const key = normalizeQueryKey(query);
    if (!key || key === "unknown") return null;
    try {
      const snap = await getDoc(doc(db, "drug_aliases", key));
      const drugId = snap.data()?.drug_id;
      return typeof drugId === "string" && drugId.length > 0 ? drugId : null;
    } catch (error) {
      console.error("Alias fast-path lookup failed:", error);
      return null;
    }
  };

  // Create (or reattach to) the drug_searches doc for an explicit query string
  // and stream updates. Force regeneration is admin-only during the pilot.
  // `forceNew` skips reattach so a doctor retry always schedules a fresh attempt;
  // `skipFastPath` keeps a dangling-alias fallback from looping back here.
  const runSearch = async (
    query: string,
    // `internal` marks the dangling-alias / orphan fallbacks: they continue a
    // search the user already dispatched, so they fire no analytics of their
    // own (one user intent = one submitted + one outcome event).
    opts?: { forceNew?: boolean; skipFastPath?: boolean; internal?: boolean },
  ) => {
    setSearchUiError(null);
    if (!opts?.internal) trackSearchSubmitted(query);

    // Library-first, mirroring the server's own serve order: an alias hit
    // paints the cached report after a single Firestore read instead of a
    // trigger round trip. A retry (forceNew) bypasses it deliberately.
    if (!opts?.forceNew && !opts?.skipFastPath) {
      const libraryDrugId = await lookupLibraryDrugId(query);
      if (libraryDrugId) {
        unsubscribeRef.current?.();
        unsubscribeRef.current = null;
        setDrugSearch(null);
        fastPathFallbackRef.current = null;
        setFastPathDrugId(libraryDrugId);
        if (!opts?.internal) trackSearchCompleted({ query, cache_hit: true });
        return;
      }
    }
    setFastPathDrugId(null);

    try {
      const newId = uuidv4();
      const existing = opts?.forceNew ? null : await findReusableSearch(query);
      const docId =
        existing ||
        (await addDocument(
          "drug_searches",
          {
            id: newId,
            user_id: user!.uid,
            query,
            status: "pending",
            created_at: new Date().toISOString(),
          },
          newId,
        ));

      // Optimistic pending state so retry immediately leaves the failed panel.
      // When reattaching to an existing doc, wait for the snapshot — it may
      // already be completed (refresh of a prior report).
      if (opts?.forceNew || !existing) {
        setDrugSearch({
          id: docId,
          user_id: user!.uid,
          query,
          status: "pending",
          created_at: new Date().toISOString(),
        });
      }

      unsubscribeRef.current?.();
      unsubscribeRef.current = subscribeToDocument<DrugSearch>(
        "drug_searches",
        docId,
        (updatedData) => {
          setDrugSearch(updatedData);
          setSearchUiError(null);
          // Outcome events carry the query (product decision) and the cache
          // flag, but never the drug id — the completed doc is just a library
          // pointer.
          if (
            !opts?.internal &&
            updatedData &&
            (updatedData.status === "completed" ||
              updatedData.status === "failed")
          ) {
            const outcomeKey = `${updatedData.id}:${updatedData.status}`;
            if (trackedOutcomeRef.current !== outcomeKey) {
              trackedOutcomeRef.current = outcomeKey;
              if (updatedData.status === "completed") {
                trackSearchCompleted({
                  query: updatedData.query,
                  cache_hit: false,
                });
              } else {
                trackSearchFailed({ query: updatedData.query });
              }
            }
          }
        },
        (error) => {
          console.error("Subscription error:", error);
          setSearchUiError(
            "Straciliśmy połączenie z wyszukiwaniem. Spróbuj ponownie.",
          );
        },
      );
    } catch (error) {
      console.error("Error creating drug search:", error);
      setSearchUiError(
        "Nie udało się uruchomić wyszukiwania. Sprawdź połączenie i spróbuj ponownie.",
      );
    }
  };

  // Latest runSearch for the URL and fallback effects below: runSearch is
  // recreated every render, so depending on it directly would re-run those
  // effects each render; the ref keeps them keyed to actual state changes only.
  const runSearchRef = useRef(runSearch);
  useEffect(() => {
    runSearchRef.current = runSearch;
  });

  // A dangling alias (drug deleted, no versions yet, unreadable report) must
  // not strand the user on a blank screen: when the fast path finishes loading
  // with nothing to show, hand the query to the backend flow — skipFastPath so
  // the same alias can't loop us back here. fastPathFallbackRef de-dupes per
  // alias id (runSearch clears it on each new fast-path hit), so every explicit
  // search attempt gets exactly one fallback and never a duplicate.
  useEffect(() => {
    if (!fastPathDrugId || reportState !== "missing") return;
    if (fastPathFallbackRef.current === fastPathDrugId) return;
    fastPathFallbackRef.current = fastPathDrugId;
    setFastPathDrugId(null);
    if (decodedQuery) {
      void runSearchRef.current(decodedQuery, {
        skipFastPath: true,
        internal: true,
      });
    }
  }, [fastPathDrugId, reportState, decodedQuery]);

  // Same guarantee for the backend flow: a completed search whose pointer no
  // longer resolves (drug purged while attached, or a legacy doc with no
  // drug_id) regenerates instead of showing nothing. forceNew skips reattach so
  // we can't pick the same dead doc back up; the ref de-dupes per search id, so
  // a doc gets exactly one regeneration and the fresh one is free to fall back
  // again if it too ends up orphaned.
  const orphanFallbackRef = useRef<string | null>(null);
  const drugSearchId = drugSearch?.id;
  useEffect(() => {
    if (!completedWithoutLibraryReport || !drugSearchId || !decodedQuery) return;
    if (orphanFallbackRef.current === drugSearchId) return;
    orphanFallbackRef.current = drugSearchId;
    void runSearchRef.current(decodedQuery, { forceNew: true, internal: true });
  }, [completedWithoutLibraryReport, drugSearchId, decodedQuery]);

  const retrySearch = async () => {
    if (!decodedQuery || retrying) return;
    setRetrying(true);
    try {
      lastSearchedRef.current = decodedQuery;
      await runSearch(decodedQuery, { forceNew: true });
    } finally {
      setRetrying(false);
    }
  };

  const reportStuckSearch = async () => {
    if (!user || !drugSearch) return;
    try {
      const id = uuidv4();
      await addDocument(
        "feedback",
        {
          id,
          scope: "search",
          sentiment: "negative",
          category: "speed",
          drug_id: null,
          report_version: null,
          query: drugSearch.query,
          search_id: drugSearch.id,
          trace_id: drugSearch.results?.trace_id ?? null,
          error_kind: "stuck_client_timeout",
          user_id: user.uid,
          status: "new",
          created_at: new Date().toISOString(),
          text: "Zgłoszenie: generowanie trwa nietypowo długo",
        },
        id,
      );
    } catch (error) {
      console.error("Failed to report stuck search:", error);
    }
  };

  // Navigating back to "/" clears the page — during render (the derived-state
  // pattern), so no committed frame pairs the empty URL with the old search.
  const [trackedDecodedQuery, setTrackedDecodedQuery] = useState(decodedQuery);
  if (decodedQuery !== trackedDecodedQuery) {
    setTrackedDecodedQuery(decodedQuery);
    if (!decodedQuery) {
      setDrugSearch(null);
      setFastPathDrugId(null);
      setSearchUiError(null);
    }
  }

  // The URL param drives the actual search: navigating to /<query> (or sharing
  // such a link) triggers it; navigating back to "/" clears the page. The
  // search-box label that used to be synced here lives in SearchBox now, keyed
  // to the same decodedQuery.
  useEffect(() => {
    if (!user) return;

    if (!decodedQuery) {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      lastSearchedRef.current = null;
      return;
    }

    if (lastSearchedRef.current === decodedQuery) return;
    lastSearchedRef.current = decodedQuery;
    void runSearchRef.current(decodedQuery);
  }, [decodedQuery, user]);

  useEffect(() => () => unsubscribeRef.current?.(), []);

  return {
    drugSearch,
    fastPathDrugId,
    fastPathLoading,
    fastPathReady,
    isLoading,
    searchUiError,
    retrying,
    reportState,
    displayReport,
    displayReportVersion,
    displayGeneratedAt,
    retrySearch,
    reportStuckSearch,
  };
}
